import { useEffect, useMemo, useState } from "react";
import { Loader2, Image as ImageIcon, RotateCcw, Video as VideoIcon } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";

export type UserMediaGalleryKind = "image" | "video" | "all";

export type UserMediaGalleryItem = {
  id: string;
  url: string;
  name: string;
  kind: "image" | "video";
  createdAt: string;
};

type UserMediaGalleryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: UserMediaGalleryItem) => void;
  mediaKind?: UserMediaGalleryKind;
  title?: string;
  description?: string;
  emptyMessage?: string;
};

function inferItemKind(type: unknown, url: string): "image" | "video" | null {
  const normalizedType = typeof type === "string" ? type.toLowerCase() : "";
  const normalizedUrl = url.toLowerCase();

  if (
    normalizedType.includes("video") ||
    /\.(mp4|mov|webm|m4v|avi)(\?|$)/i.test(normalizedUrl)
  ) {
    return "video";
  }

  if (
    normalizedType.includes("image") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(normalizedUrl)
  ) {
    return "image";
  }

  return null;
}

export default function UserMediaGalleryDialog({
  open,
  onOpenChange,
  onSelect,
  mediaKind = "all",
  title,
  description,
  emptyMessage,
}: UserMediaGalleryDialogProps) {
  const { t } = useTranslation();
  // Defaults come from the locale bundle, not from parameter defaults, so a
  // caller that passes nothing still gets copy in the active language.
  const dialogTitle = title ?? t("media.gallery.title");
  const dialogDescription = description ?? t("media.gallery.description");
  const dialogEmpty = emptyMessage ?? t("media.gallery.empty");
  const [items, setItems] = useState<UserMediaGalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by "Try again" to run the load once more.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadGallery = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await api.getUserVideoRecords(true);
        if (cancelled) return;

        if (!result.success || !result.data?.rows) {
          throw new Error(result.error || t("media.gallery.loadFailed"));
        }

        const nextItems = result.data.rows
          .map((row, index) => {
            const url = String(row.url || row.result_url || row.outputUrl || "").trim();
            if (!url) return null;

            const kind = inferItemKind(row.type || row.media_type, url);
            if (!kind) return null;

            return {
              id: String(row.id || row.job_id || `${url}-${index}`),
              url,
              kind,
              name: String(row.title || row.name || t("media.gallery.itemFallback", { n: index + 1 })),
              createdAt: String(row.createdAt || row.created_at || new Date().toISOString()),
            } satisfies UserMediaGalleryItem;
          })
          .filter((item): item is UserMediaGalleryItem => Boolean(item))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        setItems(nextItems);
      } catch (loadError) {
        if (cancelled) return;
        // People see plain words, never a raw error ("Unexpected end of JSON input"
        // from a 502 page or a dropped connection). The detail goes to the console.
        console.warn("Gallery load failed:", loadError);
        setError(t("media.gallery.loadFailed"));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadGallery();

    return () => {
      cancelled = true;
    };
  }, [open, t, attempt]);

  const filteredItems = useMemo(() => {
    if (mediaKind === "all") return items;
    return items.filter((item) => item.kind === mediaKind);
  }, [items, mediaKind]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {isLoading ? (
            <div className="flex min-h-48 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("media.gallery.loading")}
            </div>
          ) : error ? (
            <div role="alert" className="space-y-3 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground">
              <p>{error}</p>
              <Button type="button" variant="outline" onClick={() => setAttempt((n) => n + 1)}>
                <RotateCcw aria-hidden className="h-4 w-4" />
                {t("media.gallery.retry")}
              </Button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
              {dialogEmpty}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredItems.map((item) => (
                <div key={item.id} className="overflow-hidden rounded-lg border bg-card">
                  <div className="aspect-square bg-muted">
                    {item.kind === "image" ? (
                      <img src={item.url} alt={item.name} className="h-full w-full object-cover" />
                    ) : (
                      <video src={item.url} className="h-full w-full object-cover" muted playsInline />
                    )}
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                      {item.kind === "image" ? (
                        <ImageIcon className="h-3.5 w-3.5" />
                      ) : (
                        <VideoIcon className="h-3.5 w-3.5" />
                      )}
                      {item.kind === "image" ? t("media.gallery.kindImage") : t("media.gallery.kindVideo")}
                    </div>
                    <div>
                      <div className="truncate text-sm font-medium">{item.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(item.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => {
                        onSelect(item);
                        onOpenChange(false);
                      }}
                    >
                      {item.kind === "image" ? t("media.gallery.chooseImage") : t("media.gallery.chooseVideo")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
